/**
 * `useFeatureFlags` — renderer-side mirror of the main process feature
 * flag snapshot. Fetches `app:get-feature-flags` once at component
 * mount and caches the result for the lifetime of the workspace.
 *
 * Per TDD §17.2 the flag IS NOT hot-reloadable in production —
 * toggling `CRUCIBLE_INTERVIEW_ENGINE_V1` requires a Foundry restart.
 * That makes a `staleTime: Infinity` cache the right shape: the value
 * is read once at app start (main side), pushed to the renderer at
 * workspace mount, and never invalidated.
 *
 * The hook is the SOLE renderer-side gate for the interview engine
 * subscriptions (`<InterviewSlotOverlay>`'s effect bails out when
 * `loaded && !interviewEngineV1`). Per the §17.2 contract,
 * subscriptions are gated **at registration time**, not at handler
 * entry — so callers MUST check `loaded` AND `interviewEngineV1`
 * before registering listeners. The pre-load pre-render state has
 * `loaded: false` and the consumer should treat that as "don't
 * subscribe yet"; the next render after the query resolves flips
 * `loaded: true` and the effect fires.
 */

import { useQuery } from '@tanstack/react-query';
import type { FeatureFlagsSnapshot } from '@volley/crucible-ipc-types/ipc-channels';

const FLAG_QUERY_OPTIONS = {
  // The flag is process-lifetime stable — a refetch during a single
  // workspace session would never see a different value, and a
  // bouncing query against the main process is wasted IPC.
  staleTime: Infinity,
  // No retries — the IPC handler is registered at app start and is
  // there for the entire process. A single failure is structural;
  // keep the surface honest.
  retry: false,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

/**
 * Default snapshot used while the IPC query is in flight. Mirrors the
 * main-side default (engine ON) but with `loaded: false` so callers
 * can distinguish "haven't fetched yet" from "fetched and ON" — only
 * `loaded: true` should drive subscription registration.
 */
const DEFAULT_SNAPSHOT: FeatureFlagsSnapshot = {
  interviewEngineV1: false,
  agentTransparency: false,
  // `lazyDashboardLoad` is opt-OUT on the main side (default ON;
  // `CRUCIBLE_LAZY_DASHBOARD_LOAD=0` disables — issue #778 flip).
  // The pre-load placeholder here stays `false` for the same reason
  // `agentTransparency` does: the Dashboard gates list queries on
  // `loaded && lazyPath`, so neither IPC fires during the in-flight
  // window. Setting the placeholder to `true` would risk briefly
  // routing through the lazy path before flags resolve if any
  // consumer ever stopped gating on `loaded`. Mirrors the main-side
  // reader at `feature-flags.ts:isLazyDashboardLoadEnabled`.
  lazyDashboardLoad: false,
  foundryPrototypes: false,
  // Expert level-design tooling — opt-IN (default OFF). Mirrors the main-side reader at
  // `feature-flags.ts:isLevelDesignProEnabled`. Conservative false pre-load placeholder.
  levelDesignPro: false,
  // Phase 0a cloud-VM v1 gate — opt-IN (default OFF). Mirrors the
  // main-side reader at `feature-flags.ts:isCloudVmV1Enabled`.
  cloudVmV1: false,
  // Unified Cloud project creation remains opt-IN until the Furnace
  // default-deny egress prerequisite has been verified.
  cloudProjectPlacement: false,

  // New-UI refresh gate. The MAIN-side reader is opt-OUT (default ON) —
  // see `feature-flags.ts:isNewUiEnabled`. This pre-load PLACEHOLDER is
  // intentionally kept `false` (conservative, like every other flag here):
  // it only applies in the sub-50ms window before `app:get-feature-flags`
  // resolves, after which the real value (ON by default) takes over. A
  // `true` placeholder would flip the many component tests that rely on
  // the default snapshot (rather than mocking the flag) into new-UI mode.
  newUi: false,
  // Craft Mode — opt-IN (default OFF). Mirrors the main-side reader at
  // `feature-flags.ts:isCraftModeEnabled`. Requires `newUi` to also be true.
  craftMode: false,
  // The one collapsed intake gate (crucible#3313 — formerly the identical
  // interviewIntegrated / intakePreview / northStar trio). Mirrors the
  // main-side reader at `feature-flags.ts:isIntakeEnabled`. Conservative
  // false pre-load placeholder like every other flag here.
  intake: false,
  // Live build-preview gate — opt-OUT (default ON). Mirrors the main-side
  // reader at `feature-flags.ts:isLivePreviewEnabled`. The pre-load
  // placeholder stays `false` like every other flag here so no auto-open
  // fires during the sub-50ms window before `app:get-feature-flags`
  // resolves.
  livePreview: false,
  // Scaffold-first — opt-IN (default OFF). Mirrors the main-side reader
  // at `feature-flags.ts:isScaffoldFirstEnabled`.
  scaffoldFirst: false,
  // Design tab — opt-OUT (default ON) on the main side. The pre-load
  // PLACEHOLDER stays `false` (conservative, like every flag here): it only
  // applies in the sub-50ms window before `app:get-feature-flags` resolves,
  // after which the real value (ON by default) takes over. Workspace still
  // gates on `newUi`, a loaded game, and the user config kill switch, so a
  // `false` placeholder never flashes a Design pill during that window.
  designTab: false,
  // Game-tile image upload (editor project panel) — opt-OUT, default ON.
  tileImageUpload: false,
  // Art tab (crucible#2611 Task 4) — opt-IN (default OFF). Mirrors the
  // main-side reader at `feature-flags.ts:isArtTabEnabled`. Conservative
  // false placeholder like every other flag here.
  artTab: false,
  // crucible#3052 — build-time concurrent asset-review, still being proven
  // out. Real default is ON under intake; this is mainly a main-process
  // orchestration concern (build kickoff / asset-review arming), not
  // something the renderer branches on today, so the conservative false
  // placeholder is safe here regardless.
  buildTimeAssetReview: false,
  // crucible#3041 — pre-load placeholder OFF like everything else here.
  createSplit: false,
};

export interface FeatureFlagsState {
  /** True once the `app:get-feature-flags` IPC has settled. False
   *  during the in-flight window before the first response. A failed
   *  IPC call still settles the load but keeps consumers on the conservative
   *  default snapshot; the renderer cannot infer main-process opt-out flags. */
  loaded: boolean;
  /** True iff the build-game interview engine v1 is enabled. */
  interviewEngineV1: boolean;
  /** True iff the agent-transparency uplift (Crucible #602) is
   *  enabled. Default ON since Crucible #673 (the C2 ship); only
   *  `CRUCIBLE_AGENT_TRANSPARENCY=0` in the env at app start
   *  disables it. Renderer activity surfaces (template-too-old
   *  banner, activity banner, hook subscription) MUST gate on
   *  `loaded && agentTransparency` so the flag is the only switch
   *  even if a stray push slipped through. */
  agentTransparency: boolean;
  /**
   * True iff the dashboard's lazy hydrate path is enabled. The
   * Dashboard MUST gate its `game:list:lite` / `game:list` `enabled` on
   * `loaded` per plan line 238 — issuing a list query before flags
   * resolve would briefly hit the wrong IPC. Default ON since issue
   * #778 (the flag-flip ship); only `CRUCIBLE_LAZY_DASHBOARD_LOAD=0`
   * in the env at app start disables it. The kill switch stays as
   * the emergency rollback for one further release before the legacy
   * `game:list` path is deleted entirely.
   */
  lazyDashboardLoad: boolean;
  /**
   * Cloud-VM v1 execution-path gate (Phase 0a — see TDD §A-1 + the
   * canonical `feature-flags.ts:isCloudVmV1Enabled`). Surfaced
   * renderer-side so the A-1 `<VmStatusPill>` can gate its IPC
   * subscription on `loaded && cloudVmV1` — flag-OFF environments
   * never register a `vm:connection-state` listener. Default OFF;
   * only `CRUCIBLE_CLOUD_VM_V1=1` in the env at app start flips it.
   */
  cloudVmV1: boolean;
  /** True only when unified Cloud project creation has passed its rollout gate. */
  cloudProjectPlacement: boolean;
  /** True iff Foundry prototype surfaces are enabled (`CRUCIBLE_FOUNDRY_PROTOTYPES=1`).
   *  Opt-IN, default OFF. First renderer consumer: the Concept Studio entry on
   *  the New Game page gates on `loaded && foundryPrototypes`. */
  foundryPrototypes: boolean;
  /** True iff the expert level-design tooling (design §23 — the design-judge, build→design
   *  level capture, worlds nav grouping) is enabled. Opt-IN, default OFF; only
   *  `CRUCIBLE_LEVEL_DESIGN_PRO=1` enables it. The level MODEL/validation changes are
   *  backward-compatible + data-opt-in and are NOT gated by this. */
  levelDesignPro: boolean;

  /** True iff the Slate-style new-UI visual refresh is enabled. Default
   *  ON (opt-OUT); only `CRUCIBLE_NEW_UI=0` falls back to the legacy UI. */
  newUi: boolean;
  /** True iff Craft Mode (declarative scene manifest editor) is enabled.
   *  Default OFF; only `CRUCIBLE_CRAFT_MODE=1` enables it. Requires
   *  `newUi` to also be true — all Craft surfaces live inside the new
   *  Workspace layout. */
  craftMode: boolean;
  /** True iff the primary consumer-interview gate is on (crucible#3172 —
   *  hard-pinned ON in the desktop producer; absent → false on the cloud
   *  lane). ONE resolved name for what used to be the always-identical
   *  `interviewIntegrated` / `intakePreview` / `northStar` trio
   *  (crucible#3313). When ON: the adaptive soul-intake runs at create, the
   *  TV+phone preview frame hosts the interview, and the build runs
   *  best-effort so the interview is the only user-facing question flow. */
  intake: boolean;
  /** True iff the live build-preview surface (#1408 / #1420) is enabled.
   *  Default ON; `CRUCIBLE_LIVE_PREVIEW=0` disables it. The
   *  Workspace gates its auto-open-during-build behaviour on
   *  `loaded && livePreview && livePreviewAutoOpen` — flag-OFF leaves
   *  the preview a manual click. */
  livePreview: boolean;
  /** True iff the scaffold-first build flow (#1438 / A2 #1443) is enabled.
   *  Default OFF; only `CRUCIBLE_SCAFFOLD_FIRST=1`. Gate on
   *  `scaffoldFirstActive` (below), not this alone. */
  scaffoldFirst: boolean;
  /** Weekend app pairing is a permanent Preview capability. It still
   *  requires an explicit `pairing: true` launch payload. */
  inAppPairing: true;
  /** True iff the Anvil → Foundry Design process gate is enabled. Default ON
   *  (opt-OUT); only `CRUCIBLE_DESIGN_TAB=0` disables it. Workspace layers this
   *  with `config.designTabEnabled !== false`, and create dialogs hide the
   *  "Advanced" interview-depth option when this is false. */
  designTab: boolean;
  /** True iff the editor project panel's game-tile image section is enabled.
   *  Default ON for the UI-refresh PR review (opt-OUT — set
   *  `CRUCIBLE_TILE_IMAGE_UPLOAD=0` to disable). */
  tileImageUpload: boolean;
  /** The resolved scaffold-first predicate: `scaffoldFirst && livePreview`.
   *  Scaffold-first requires live-preview (#1438 §6); this is the single
   *  DRY predicate consumers gate on, so "requires live-preview" lives in
   *  one place (an improvement on the inline `craftMode && newUi`). */
  scaffoldFirstActive: boolean;
  /** True iff the Art tab (crucible#2611 Task 4) is enabled. Default OFF;
   *  `CRUCIBLE_ART_TAB=1` force-enables it standalone. crucible#3136 — now
   *  ALSO defaults ON whenever the primary intake gate is on — the MAIN-side
   *  computation folds that in before this value reaches the wire, so this
   *  hook doesn't need to OR it in itself. Gates the Art workspace mode
   *  pill. */
  artTab: boolean;
  /** crucible#3052 — the concurrent build-time asset-review step, still
   *  being proven out. Default ON under intake; `CRUCIBLE_BUILD_TIME_ASSET_REVIEW=0`
   *  is the kill switch. Mainly a main-process orchestration concern. */
  buildTimeAssetReview: boolean;
  /** crucible#3041 — the split create (CRUCIBLE_CREATE_SPLIT, default OFF).
   *  Renderer consumers converge early create-navigation on the setup-ready
   *  relay when this is on. Coalesced `?? false` like the other opt-in flags. */
  createSplit: boolean;
}

export function useFeatureFlags(): FeatureFlagsState {
  const query = useQuery<FeatureFlagsSnapshot>({
    queryKey: ['app:get-feature-flags'],
    queryFn: async () => {
      // Test environments may not stub the IPC bridge; fall back to
      // the safe default rather than throwing inside React Query
      // (which would otherwise leak as an unhandled rejection).
      if (typeof window === 'undefined' || !window.crucible) {
        return DEFAULT_SNAPSHOT;
      }
      return await window.crucible.invoke('app:get-feature-flags');
    },
    ...FLAG_QUERY_OPTIONS,
  });
  const flags = query.data ?? DEFAULT_SNAPSHOT;
  // crucible#3313 — the one collapsed intake gate (formerly the identical
  // interviewIntegrated / intakePreview / northStar trio). Optional on the
  // wire (the cloud-VM producer does not emit it), so coalesce to false.
  const intake = flags.intake ?? false;
  const livePreview = flags.livePreview ?? false;

  return {
    loaded: query.isFetched,
    interviewEngineV1: flags.interviewEngineV1,
    agentTransparency: flags.agentTransparency,
    lazyDashboardLoad: flags.lazyDashboardLoad,
    cloudVmV1: flags.cloudVmV1,
    cloudProjectPlacement: flags.cloudProjectPlacement ?? false,
    foundryPrototypes: flags.foundryPrototypes ?? false,
    levelDesignPro: flags.levelDesignPro ?? false,

    newUi: intake || flags.newUi,
    craftMode: flags.craftMode,
    intake,
    // `livePreview` is OPTIONAL on the wire `FeatureFlagsSnapshot` (see
    // its docblock — the SDK-lane do-not-touch carve-out), so coalesce
    // to a definite `false` rather than the `boolean | undefined`
    // a success payload with an omitted field or rejected IPC would yield.
    livePreview,
    scaffoldFirst: flags.scaffoldFirst ?? false,
    // #1576 graduated pairing from feature-flag control. Keep the downstream
    // capability name while removing it from the process snapshot contract.
    inAppPairing: true,
    // Opt-OUT on the main side (default ON), but `?? false` here so the
    // in-flight / field-absent (cloud jsonrpc) cases resolve to a definite
    // `false` — the desktop producer always sends the real (default-ON) value.
    designTab: flags.designTab ?? false,
    tileImageUpload: flags.tileImageUpload ?? false,
    // Resolved predicate — scaffold-first requires live-preview (#1438 §6),
    // enforced here in one place.
    scaffoldFirstActive: (flags.scaffoldFirst ?? false) && livePreview,
    // Art tab (crucible#2611 Task 4). crucible#3136 — now defaults ON under
    // `northStar` too, but that merge happens on the MAIN side (isArtTabEnabled
    // now rides isIntakeEnabled), so the wire value already carries the
    // correct pre-merged result — no `|| northStar` needed here.
    artTab: flags.artTab ?? false,
    // crucible#3052 — build-time concurrent asset-review. Opt-OUT under
    // intake (default ON); `?? false` for the in-flight/field-absent case.
    buildTimeAssetReview: flags.buildTimeAssetReview ?? false,
    // crucible#3041 — opt-in, coalesced like the other optional wire fields.
    createSplit: flags.createSplit ?? false,
  };
}
